import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button, Box, IconButton, Modal } from "@mui/material";
import { useSelector } from "react-redux";
import { Pencil } from "lucide-react";
import SkillIcon from "nextIcon.svg";
import EditCustomSkillsModal from "./EditCustomSkillsModal";

// Category pill styles matching the recruiter job-offer SkillView:
// hard = green filled, soft = teal filled, lang = outlined.
const categoryPillSx = (cat) => {
  switch (cat) {
  case "hard":
    return { backgroundColor: "#166D4C", color: "#fff", border: "1px solid #166D4C" };
  case "soft":
    return { backgroundColor: "#023331", color: "#fff", border: "1px solid #023331" };
  case "lang":
    return { backgroundColor: "transparent", color: "#023331", border: "1px solid #166D4C" };
  default:
    return { backgroundColor: "#023331", color: "#fff", border: "1px solid #023331" };
  }
};

const CustomSkills = () => {
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const skills = currentRecruiter.company.skills;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);

  const handleNewSkillClicked = (e) => {
    e.preventDefault();
    setSelectedSkill(null);
    setIsEditModalOpen(true);
  };

  const handleSkillEditClicked = (skill) => {
    setSelectedSkill(skill);
    setIsEditModalOpen(true);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          mb: "24px",
        }}
      >
        <Box
          component="h2"
          sx={{
            fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            fontSize: "1.25rem",
            color: "hsl(178, 93%, 10%)",
            letterSpacing: "-0.01em",
            m: 0,
          }}
        >
          <FormattedMessage id="settings.custom_skills_header" />
        </Box>
        <Button
          variant="rounded"
          size="small"
          onClick={handleNewSkillClicked}
          sx={{
            minWidth: "150px",
            textTransform: "none",
            px: 1.75,
            py: 0.75,
            fontSize: "0.95rem",
            fontFamily: "Segoe UI Semibold, sans-serif",
            backgroundColor: "hsl(153 68% 25%)",
            color: "white",
            boxShadow: "none",
            transition: "all 180ms ease",
            "&:hover": {
              backgroundColor: "hsl(153 68% 22%)",
              boxShadow: "none",
            },
          }}
        >
          <FormattedMessage id="settings.custom_skills_add" />
        </Button>
      </Box>

      {skills.length > 0 ? (
        <Box
          sx={{
            border: "1px solid hsl(220, 13%, 91%)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {/* Column labels */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: "16px",
              py: "10px",
              bgcolor: "hsl(210, 20%, 98%)",
              borderBottom: "1px solid hsl(220, 13%, 91%)",
            }}
          >
            <Box
              sx={{
                flex: 1,
                fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                fontSize: "0.8125rem",
                color: "hsl(220, 10%, 46%)",
              }}
            >
              <FormattedMessage id="settings.custom_skills_header2" />
            </Box>
            <Box
              sx={{
                width: "140px",
                textAlign: "center",
                fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                fontSize: "0.8125rem",
                color: "hsl(220, 10%, 46%)",
              }}
            >
              <FormattedMessage id="settings.custom_skills_header3" />
            </Box>
            <Box sx={{ width: "48px" }} />
          </Box>

          {/* Rows */}
          {skills.map((skill, index) => (
            <Box
              key={`${skill.id}--skill`}
              sx={{
                display: "flex",
                alignItems: "center",
                px: "16px",
                py: "12px",
                borderTop:
                  index === 0 ? "none" : "1px solid hsl(220, 13%, 91%)",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "0.9375rem",
                  color: "hsl(220, 25%, 12%)",
                }}
              >
                {skill.name}
              </Box>
              <Box
                sx={{ width: "140px", display: "flex", justifyContent: "center" }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: "12px",
                    py: "4px",
                    borderRadius: "9999px",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    fontSize: "0.75rem",
                    ...categoryPillSx(skill.category),
                  }}
                >
                  <FormattedMessage
                    id={`candidate.profile.skills.${skill.category}`}
                  />
                </Box>
              </Box>
              <Box
                sx={{ width: "48px", display: "flex", justifyContent: "center" }}
              >
                <IconButton
                  onClick={() => handleSkillEditClicked(skill)}
                  size="small"
                >
                  <Pencil size={18} color="hsl(220, 10%, 46%)" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            py: "24px",
          }}
        >
          <Box
            component="p"
            sx={{
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              fontSize: "0.9375rem",
              color: "hsl(220, 25%, 12%)",
              maxWidth: "420px",
              m: 0,
              mb: "24px",
            }}
          >
            <FormattedMessage id="settings.custom_skills_empty" />
          </Box>
          <Box
            component="img"
            src={SkillIcon}
            alt="Skill Icon"
            sx={{ width: "220px", maxWidth: "100%" }}
          />
        </Box>
      )}

      <Modal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <EditCustomSkillsModal
          closeModal={() => setIsEditModalOpen(false)}
          skill={selectedSkill}
        />
      </Modal>
    </Box>
  );
};

export default CustomSkills;
